import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

import { CustomFieldInput } from './CustomFieldInput';
import { ColumnDef, ColumnKind } from './columns';
import { formatReadOnlyValue } from './customFields';

/** Kinds only custom fields use; the grid renders them through {@link CustomFieldInput}. */
const CUSTOM_ONLY_KINDS: ReadonlySet<ColumnKind> = new Set([
    'integer',
    'decimal',
    'enum',
    'long_text',
]);

// `data-grid-cell` marks the actual focusable node for keyboard navigation
// (see `gridNavigation.ts`); `tabIndex={-1}` is the roving-tabindex default —
// exactly one cell in the grid is promoted to `tabIndex={0}` at a time, but
// that promotion happens imperatively on the DOM node in `RowEditor.tsx`, not
// through a prop here, so this stays a hardcoded constant that never varies
// and can't affect the `RowCells` `React.memo`.

export function Cell({
    kind,
    value,
    onChange,
    errorId,
    column,
}: {
    kind: ColumnKind;
    value: unknown;
    onChange: (value: unknown) => void;
    /** Id of the visible error message for this cell; marks the cell invalid. */
    errorId?: string;
    /** The column's definition; lets a custom field draw its own widget and limits. */
    column?: ColumnDef;
}) {
    const gridCellProps = {
        'data-grid-cell': 'true',
        tabIndex: -1,
        'aria-invalid': errorId ? true : undefined,
        'aria-describedby': errorId,
    } as const;

    if (column?.readOnly) {
        return (
            <span
                className="flex h-8 min-w-32 items-center text-sm text-muted-foreground"
                data-testid="Cell__readOnly"
            >
                {formatReadOnlyValue(value)}
            </span>
        );
    }

    if (column?.custom || CUSTOM_ONLY_KINDS.has(kind)) {
        return (
            <CustomFieldInput
                field={column ?? { key: '', label: '', kind }}
                value={value}
                onChange={onChange}
                compact
                controlProps={gridCellProps}
            />
        );
    }

    if (kind === 'bool') {
        return (
            <Checkbox
                checked={value === true}
                onCheckedChange={(v) => onChange(v === true)}
                {...gridCellProps}
            />
        );
    }

    if (kind === 'number') {
        return (
            <Input
                type="number"
                value={value == null ? '' : String(value)}
                className="h-8 text-sm w-20"
                onChange={(e) => {
                    const v = e.target.value;
                    onChange(v === '' ? null : Number(v));
                }}
                {...gridCellProps}
            />
        );
    }

    if (kind === 'list') {
        const text = Array.isArray(value) ? value.join(', ') : '';
        return (
            <Input
                value={text}
                className="h-8 text-sm min-w-32"
                placeholder="a, b, c"
                onChange={(e) =>
                    onChange(
                        e.target.value
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean),
                    )
                }
                {...gridCellProps}
            />
        );
    }

    if (kind === 'date') {
        // A plain text input is the intentional first rendering for `date` —
        // no model has a date field yet, so this is unexercised today but
        // must not fall through unhandled once one does. A dedicated date
        // picker is a follow-up, not built here.
        return (
            <Input
                type="text"
                value={value == null ? '' : String(value)}
                placeholder="YYYY-MM-DD"
                className="h-8 text-sm min-w-32"
                onChange={(e) => {
                    const v = e.target.value;
                    onChange(v === '' ? null : v);
                }}
                {...gridCellProps}
            />
        );
    }

    // text
    return (
        <Input
            value={value == null ? '' : String(value)}
            className="h-8 text-sm min-w-32"
            onChange={(e) => {
                const v = e.target.value;
                onChange(v === '' ? null : v);
            }}
            {...gridCellProps}
        />
    );
}
